/**
 * Edit-mode z-depth selection cycling.
 *
 * Mirrors object-mode selectionCycle.ts: a click snapshots the selectable
 * stack under the cursor, and quick repeat clicks at the same screen-space
 * position advance through that stack nearest-to-farthest.
 */

import type { EditClickHit } from "../app/storeEditOps/index.ts";
import { CYCLE_CLICK_DIST_SQ, CYCLE_CLICK_MS } from "./interactionConstants.ts";

export type EditSelectionCycleCandidate = {
  /** Stable identity for the edit element at the active granularity. */
  key: string;
  hit: EditClickHit;
};

type EditCycleState = {
  lastX: number;
  lastY: number;
  lastTime: number;
  contextKey: string | null;
  stack: EditSelectionCycleCandidate[];
  index: number;
};

const state: EditCycleState = {
  lastX: 0,
  lastY: 0,
  lastTime: 0,
  contextKey: null,
  stack: [],
  index: 0,
};

/** Clear the edit-mode cycle stack so the next click starts at the frontmost hit. */
export function resetEditSelectionCycle(): void {
  state.lastTime = 0;
  state.contextKey = null;
  state.stack = [];
  state.index = 0;
}

/**
 * Record a click that deliberately bypassed cycling, such as additive or
 * modifier-based edit selection. This matches object-mode's non-cycle path.
 */
export function noteNonCycleEditClick(clientX: number, clientY: number): void {
  resetEditSelectionCycle();
  state.lastX = clientX;
  state.lastY = clientY;
  state.lastTime = performance.now();
}

/**
 * Resolve a plain edit-mode x-ray click against a nearest-first stack of
 * candidates at the click location.
 */
export function resolveEditSelectionCycle(
  clientX: number,
  clientY: number,
  contextKey: string,
  candidates: readonly EditSelectionCycleCandidate[],
): EditClickHit | null {
  const now = performance.now();
  const ddx = clientX - state.lastX;
  const ddy = clientY - state.lastY;

  if (state.contextKey !== contextKey) {
    resetEditSelectionCycle();
  }

  if (state.stack.length > 0) {
    const freshByKey = new Map(candidates.map((c) => [c.key, c]));
    const refreshedStack: EditSelectionCycleCandidate[] = [];
    let valid = true;
    for (const target of state.stack) {
      const fresh = freshByKey.get(target.key);
      if (!fresh) {
        valid = false;
        break;
      }
      refreshedStack.push(fresh);
    }
    if (valid) {
      state.stack = refreshedStack;
    } else {
      resetEditSelectionCycle();
    }
  }

  const withinWindow =
    state.stack.length > 1 &&
    state.contextKey === contextKey &&
    now - state.lastTime < CYCLE_CLICK_MS &&
    ddx * ddx + ddy * ddy < CYCLE_CLICK_DIST_SQ;

  if (withinWindow) {
    state.index = (state.index + 1) % state.stack.length;
    state.lastTime = now;
    return state.stack[state.index]?.hit ?? null;
  }

  state.stack = [...candidates];
  state.index = 0;
  state.lastX = clientX;
  state.lastY = clientY;
  state.lastTime = now;
  state.contextKey = contextKey;
  return state.stack[0]?.hit ?? null;
}
